import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid2 as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import service, {
  CategoriaSlot,
  Rotacao,
  RotacaoSlot,
} from '../../services/marketing-rotacao.service';
import { PageHeader, StatCard, EmptyState } from '../../components/Marketing';
import {
  Repeat,
  CalendarMonthOutlined,
  TimerOutlined,
  SavingsOutlined,
} from '@mui/icons-material';

const CATEGORIA_LABELS: Record<CategoriaSlot, string> = {
  food: 'Pagali Food',
  shop: 'Pagali Shop',
  events: 'Pagali Events',
  parceiros: 'Parceiros',
  custom: 'Personalizado',
};

const CATEGORIA_COLORS: Record<CategoriaSlot, 'primary' | 'secondary' | 'success' | 'warning' | 'default'> = {
  food: 'warning',
  shop: 'primary',
  events: 'secondary',
  parceiros: 'success',
  custom: 'default',
};

const STATUS_COLORS: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  rascunho: 'default',
  ativa: 'success',
  terminada: 'default',
  cancelada: 'error',
  pendente: 'info',
};

const formatCve = (v?: number | null) => (v == null ? '—' : `${Number(v).toLocaleString('pt-PT')} CVE`);
const formatDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString('pt-PT', { timeZone: 'UTC' }) : '—');

export default function CashbackRotativo() {
  const [rotacoes, setRotacoes] = useState<Rotacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Rotacao | null>(null);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    nome: '',
    descricao: '',
    data_inicio: '',
    num_semanas: 4,
    budget_total: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRotacoes(await service.list());
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetails = useCallback(async (id: number) => {
    try {
      setSelected(await service.get(id));
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm({ nome: '', descricao: '', data_inicio: '', num_semanas: 4, budget_total: '' });
    setCreateOpen(true);
  };

  const create = async () => {
    if (!form.nome.trim() || !form.data_inicio) {
      setToast({ msg: 'Nome e data início obrigatórios', severity: 'error' });
      return;
    }
    try {
      await service.create({
        nome: form.nome.trim(),
        descricao: form.descricao || undefined,
        data_inicio: form.data_inicio,
        num_semanas: form.num_semanas,
        budget_total: form.budget_total ? Number(form.budget_total) : undefined,
      });
      setCreateOpen(false);
      setToast({ msg: 'Rotação criada — edite os slots e ative', severity: 'success' });
      await load();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Eliminar rotação?')) return;
    try {
      await service.remove(id);
      setToast({ msg: 'Eliminada', severity: 'success' });
      await load();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    }
  };

  const activateSlot = async (slot: RotacaoSlot) => {
    if (!selected) return;
    if (!window.confirm(`Ativar semana ${slot.semana_num} (${slot.titulo}) — ${slot.percentagem}% cashback?`)) return;
    try {
      await service.activateSlot(selected.ID_ROTACAO, slot.id);
      setToast({ msg: 'Slot ativado — campanha de cashback criada', severity: 'success' });
      await loadDetails(selected.ID_ROTACAO);
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    }
  };

  const endSlot = async (slot: RotacaoSlot) => {
    if (!selected) return;
    if (!window.confirm('Encerrar slot?')) return;
    try {
      await service.endSlot(selected.ID_ROTACAO, slot.id);
      setToast({ msg: 'Slot encerrado', severity: 'success' });
      await loadDetails(selected.ID_ROTACAO);
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    }
  };

  const updateSlotField = async (slot: RotacaoSlot, field: keyof RotacaoSlot, value: any) => {
    if (!selected) return;
    try {
      await service.updateSlot(selected.ID_ROTACAO, slot.id, { [field]: value });
      await loadDetails(selected.ID_ROTACAO);
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    }
  };

  // Detalhes
  if (selected) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
          <IconButton onClick={() => setSelected(null)}>
            <BackIcon />
          </IconButton>
          <Box flex={1}>
            <Typography variant="h5" fontWeight={600}>
              {selected.NOME}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selected.DESCRICAO || 'Sem descrição'}
            </Typography>
          </Box>
          <Chip label={selected.STATUS} color={STATUS_COLORS[selected.STATUS]} />
          <Button startIcon={<RefreshIcon />} onClick={() => loadDetails(selected.ID_ROTACAO)}>
            Atualizar
          </Button>
        </Stack>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 3, mb: 3 }}>
          <StatCard icon={<CalendarMonthOutlined />} label="Início" value={formatDate(selected.DATA_INICIO)} color="info" />
          <StatCard icon={<TimerOutlined />} label="Nº semanas" value={selected.NUM_SEMANAS} color="primary" />
          <StatCard icon={<SavingsOutlined />} label="Orçamento total" value={formatCve(selected.BUDGET_TOTAL)} color="warning" />
        </Box>

        <Grid container spacing={3}>
          {(selected.slots || []).map((slot) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={slot.id}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="overline" color="text.secondary">
                      Semana {slot.semana_num}
                    </Typography>
                    <Chip size="small" label={slot.status} color={STATUS_COLORS[slot.status]} />
                  </Stack>

                  <Stack spacing={1.5}>
                    <TextField
                      size="small"
                      label="Título"
                      value={slot.titulo}
                      disabled={slot.status === 'ativa' || slot.status === 'terminada'}
                      onChange={(e) => setSelected({
                        ...selected,
                        slots: selected.slots!.map((s) => (s.id === slot.id ? { ...s, titulo: e.target.value } : s)),
                      })}
                      onBlur={(e) => updateSlotField(slot, 'titulo', e.target.value)}
                    />
                    <FormControl size="small" fullWidth>
                      <InputLabel>Categoria</InputLabel>
                      <Select
                        label="Categoria"
                        value={slot.categoria}
                        disabled={slot.status === 'ativa' || slot.status === 'terminada'}
                        onChange={(e) => updateSlotField(slot, 'categoria', e.target.value as CategoriaSlot)}
                      >
                        {Object.entries(CATEGORIA_LABELS).map(([k, v]) => (
                          <MenuItem key={k} value={k}>
                            {v}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        size="small"
                        type="number"
                        label="Cashback %"
                        value={slot.percentagem}
                        disabled={slot.status === 'ativa' || slot.status === 'terminada'}
                        onBlur={(e) => updateSlotField(slot, 'percentagem', Number(e.target.value))}
                        onChange={(e) => setSelected({
                          ...selected,
                          slots: selected.slots!.map((s) =>
                            s.id === slot.id ? { ...s, percentagem: Number(e.target.value) } : s
                          ),
                        })}
                        inputProps={{ min: 0, max: 100, step: 0.5 }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label="Budget CVE"
                        value={slot.budget_semana ?? ''}
                        disabled={slot.status === 'ativa' || slot.status === 'terminada'}
                        onBlur={(e) =>
                          updateSlotField(slot, 'budget_semana', e.target.value ? Number(e.target.value) : null)
                        }
                        onChange={(e) => setSelected({
                          ...selected,
                          slots: selected.slots!.map((s) =>
                            s.id === slot.id ? { ...s, budget_semana: e.target.value ? Number(e.target.value) : null } : s
                          ),
                        })}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(slot.data_inicio)} → {formatDate(slot.data_fim)}
                    </Typography>

                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Chip
                        size="small"
                        label={CATEGORIA_LABELS[slot.categoria]}
                        color={CATEGORIA_COLORS[slot.categoria]}
                      />
                      {slot.status === 'pendente' && (
                        <Tooltip title="Ativar slot (cria campanha)">
                          <IconButton size="small" color="success" onClick={() => activateSlot(slot)}>
                            <PlayIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {slot.status === 'ativa' && (
                        <Tooltip title="Encerrar">
                          <IconButton size="small" color="warning" onClick={() => endSlot(slot)}>
                            <StopIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)}>
          {toast ? (
            <Alert severity={toast.severity} onClose={() => setToast(null)}>
              {toast.msg}
            </Alert>
          ) : undefined}
        </Snackbar>
      </Box>
    );
  }

  // Lista
  return (
    <Box>
      <PageHeader
        title="Cashback Rotativo Semanal"
        subtitle="Planeie N semanas com categorias e percentagens diferentes"
        icon={<Repeat />}
        actions={
          <>
            <Button startIcon={<RefreshIcon />} onClick={load}>Atualizar</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Nova Rotação</Button>
          </>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Cada rotação define N semanas com diferentes categorias/percentagens (ex: Sem 1 Food 10%, Sem 2 Shop 7%...).
        Ao ativar um slot, uma campanha real é criada em <code>pg_cashback_campaigns</code>.
      </Alert>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Início</TableCell>
                <TableCell align="right">Semanas</TableCell>
                <TableCell align="right">Slots ativos</TableCell>
                <TableCell align="right">Budget</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rotacoes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <EmptyState
                      icon={<Repeat />}
                      title="Sem rotações configuradas"
                      description="Crie uma rotação com N semanas — cada semana pode ter uma categoria e percentagem diferentes (Food 10%, Shop 7%, ...)."
                      action={
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                          Nova Rotação
                        </Button>
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : (
                rotacoes.map((r) => (
                  <TableRow key={r.ID_ROTACAO} hover style={{ cursor: 'pointer' }} onClick={() => loadDetails(r.ID_ROTACAO)}>
                    <TableCell>{r.NOME}</TableCell>
                    <TableCell>{formatDate(r.DATA_INICIO)}</TableCell>
                    <TableCell align="right">{r.NUM_SEMANAS}</TableCell>
                    <TableCell align="right">
                      {r.slots_ativos ?? 0} / {r.total_slots ?? 0}
                    </TableCell>
                    <TableCell align="right">{formatCve(r.BUDGET_TOTAL)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.STATUS} color={STATUS_COLORS[r.STATUS]} />
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      {(r.STATUS === 'rascunho' || r.STATUS === 'cancelada' || r.STATUS === 'terminada') && (
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => remove(r.ID_ROTACAO)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova Rotação</DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Nome"
              placeholder="Ex: Rotação Pagali Praia Out/26"
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              autoFocus
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Descrição"
              placeholder="Objetivo desta rotação — ex: 4 semanas rotando categorias"
              value={form.descricao}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })}
            />
            <TextField
              fullWidth
              type="date"
              label="Data de início"
              InputLabelProps={{ shrink: true }}
              value={form.data_inicio}
              onChange={(e) => setForm({ ...form, data_inicio: e.target.value })}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr' }, gap: 3 }}>
              <TextField
                fullWidth
                type="number"
                label="Nº semanas"
                value={form.num_semanas}
                onChange={(e) => setForm({ ...form, num_semanas: Number(e.target.value) })}
                inputProps={{ min: 1, max: 52 }}
              />
              <TextField
                fullWidth
                type="number"
                label="Orçamento total (CVE)"
                value={form.budget_total}
                onChange={(e) => setForm({ ...form, budget_total: e.target.value })}
              />
            </Box>
            <Alert severity="info">
              Os {form.num_semanas} slots são criados automaticamente (5% cashback cada) a partir da data de início.
              Pode editar cada slot na tela de detalhes.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={create}>
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)}>
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
